import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api } from "../lib/api";

const ProspectsContext = createContext(null);

export const ProspectsProvider = ({ children }) => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch semua prospek dari backend (scope otomatis berdasarkan role JWT)
  const fetchProspects = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/prospects", { params });
      setProspects(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Gagal memuat data prospek");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    fetchProspects();
  }, [fetchProspects]);

  useEffect(() => {
    const refresh = () => fetchProspects();
    window.addEventListener("hscis:prospect-saved", refresh);
    window.addEventListener("hscis:coverage-checkin-saved", refresh);
    return () => {
      window.removeEventListener("hscis:prospect-saved", refresh);
      window.removeEventListener("hscis:coverage-checkin-saved", refresh);
    };
  }, [fetchProspects]);

  // Tambah prospek baru via API
  const addProspect = useCallback(async (payload) => {
    const res = await api.post("/prospects", payload);
    setProspects((prev) => [res.data, ...prev]);
    return res.data;
  }, []);

  // Update status prospek
  const updateStatus = useCallback(async (id, status) => {
    const res = await api.patch(`/prospects/${id}`, { status });
    setProspects((prev) => prev.map((p) => (p.id === id ? res.data : p)));
    return res.data;
  }, []);

  // Update field lain
  const updateProspect = useCallback(async (id, patch) => {
    const res = await api.patch(`/prospects/${id}`, patch);
    setProspects((prev) => prev.map((p) => (p.id === id ? res.data : p)));
    return res.data;
  }, []);

  // Hapus prospek
  const deleteProspect = useCallback(async (id) => {
    await api.delete(`/prospects/${id}`);
    setProspects((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // Cari by ID dari state lokal
  const getById = useCallback(
    (id) => prospects.find((p) => p.id === id),
    [prospects]
  );

  return (
    <ProspectsContext.Provider
      value={{
        prospects,
        loading,
        error,
        fetchProspects,
        addProspect,
        updateStatus,
        updateProspect,
        deleteProspect,
        getById,
      }}
    >
      {children}
    </ProspectsContext.Provider>
  );
};

export const useProspects = () => {
  const ctx = useContext(ProspectsContext);
  if (!ctx) throw new Error("useProspects must be used inside ProspectsProvider");
  return ctx;
};
